/**
 * SupplierVerificationTrustBoundary (AJIP-16.02.008 · WS21)
 * ---------------------------------------------------------------------
 * Founder Ruling S4 — the Supplier Journey records only that an
 * external verification outcome was received. It MUST NOT manufacture
 * verification truth, trust scores, or authority signals.
 *
 * This module is:
 *   - Validation-only.
 *   - Provider-independent.
 *   - Free of external-system imports (no admin RPC, no trust engine).
 *   - Free of storage access.
 *   - Free of customer-facing behaviour.
 *   - Without authority over eligibility, activation, progression or
 *     completion.
 *
 * The boundary validates the SHAPE of an outcome payload only. It does
 * NOT infer, adjust, weight, or interpret the outcome. Any downstream
 * consumer (Trust Intelligence, admin dashboards) reads its own truth
 * from its own governed source, never from this journey.
 */
import { SupplierVerificationTrustBoundaryError } from './SupplierJourneyErrors';

const VERIFICATION_OUTCOME_KINDS = Object.freeze([
  'VERIFICATION_APPROVED',
  'VERIFICATION_REJECTED',
  'VERIFICATION_PENDING_ADDITIONAL_EVIDENCE',
  'VERIFICATION_WITHDRAWN',
  'VERIFICATION_EXPIRED',
]);

const MAX_ISSUER_LEN = 128;
const MAX_REFERENCE_LEN = 128;
const MAX_NOTE_LEN = 512;
const REF_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:@\-/]{0,127}$/;

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;

/**
 * Validate the SHAPE of a verification outcome payload. Passing does
 * not imply the outcome is true. It only asserts the caller supplied
 * a structurally acceptable record for filing.
 */
const validateVerificationOutcome = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new SupplierVerificationTrustBoundaryError('payload must be plain object');
  }
  if (!VERIFICATION_OUTCOME_KINDS.includes(payload.outcomeKind)) {
    throw new SupplierVerificationTrustBoundaryError(
      `outcomeKind must be one of: ${VERIFICATION_OUTCOME_KINDS.join(', ')}`,
    );
  }
  if (!isNonEmptyString(payload.issuerRef) || payload.issuerRef.length > MAX_ISSUER_LEN) {
    throw new SupplierVerificationTrustBoundaryError('issuerRef must be non-empty string within length cap');
  }
  if (!isNonEmptyString(payload.outcomeReferenceRef) || payload.outcomeReferenceRef.length > MAX_REFERENCE_LEN) {
    throw new SupplierVerificationTrustBoundaryError('outcomeReferenceRef must be non-empty string within length cap');
  }
  if (!REF_PATTERN.test(payload.outcomeReferenceRef)) {
    throw new SupplierVerificationTrustBoundaryError('outcomeReferenceRef has invalid shape');
  }
  if (payload.recordedAt !== undefined) {
    if (typeof payload.recordedAt !== 'string' || Number.isNaN(Date.parse(payload.recordedAt))) {
      throw new SupplierVerificationTrustBoundaryError('recordedAt must be ISO-8601 string');
    }
  }
  if (payload.note !== undefined) {
    if (typeof payload.note !== 'string' || payload.note.length > MAX_NOTE_LEN) {
      throw new SupplierVerificationTrustBoundaryError('note must be short string');
    }
  }
  // Governance-forbidden fields — the boundary MUST NOT let the journey
  // author trust scores, weights, or verification truth.
  const FORBIDDEN = ['trustScore', 'trustWeight', 'verificationScore', 'authority', 'verdict'];
  for (const k of FORBIDDEN) {
    if (Object.prototype.hasOwnProperty.call(payload, k)) {
      throw new SupplierVerificationTrustBoundaryError(`Field '${k}' is prohibited (S4)`);
    }
  }
  return true;
};

/**
 * Produce a frozen outcome record for filing. Does NOT normalise,
 * weight, score, or evaluate the outcome. Represents that an external
 * outcome was received — nothing more.
 */
const recordVerificationOutcome = (payload) => {
  validateVerificationOutcome(payload);
  const frozen = Object.freeze({
    outcomeKind: payload.outcomeKind,
    issuerRef: payload.issuerRef,
    outcomeReferenceRef: payload.outcomeReferenceRef,
    recordedAt: payload.recordedAt || new Date().toISOString(),
    note: payload.note || null,
  });
  return frozen;
};

const BOUNDARY_GOVERNANCE = Object.freeze({
  supplier_may: 'record-that-an-outcome-was-received',
  supplier_shall_never: 'manufacture-trust-truth|assign-scores|infer-verdict|weight-outcomes',
});

const BOUNDARY_NAME = 'SupplierVerificationTrustBoundary';

export {
  BOUNDARY_NAME,
  BOUNDARY_GOVERNANCE,
  VERIFICATION_OUTCOME_KINDS,
  validateVerificationOutcome,
  recordVerificationOutcome,
};
